import { catchError, filter, map, mergeMap, of } from 'rxjs'
import { addMessage } from 'Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage } from 'Model/Message/IMessage.js'
import { orderArtifactDescriptors } from 'Model/Order/OrderArtifact.js'
import { orderSlice } from 'Flux/Slice/Order/OrderSlice.js'
import { reportHandledError } from 'Services/Telemetry/index.js'
import type { Epic } from '../../../index.js'

const statusOf = (error: unknown): number | undefined => (error as { response?: { status?: number } } | null)?.response?.status

export const DownloadOrderArtifactEpic: Epic = (action$, state$, { OrderRepository, [orderSlice.name]: order }) =>
  action$.pipe(
    filter(order.downloadOrderArtifactRequest.match),
    mergeMap((action) => {
      const { orderId, artifact } = action.payload
      const { path, label } = orderArtifactDescriptors[artifact]

      return OrderRepository.downloadOrderArtifact(orderId, path).pipe(
        map((response) => {
          const link = response.data.link
          const anchor = document.createElement('a')
          anchor.href = link
          // S3 applies the presigned Content-Disposition to the object but not to its error responses, so
          // an object that vanished between the existence check and this GET would otherwise navigate the
          // SPA away to NoSuchKey XML.
          anchor.target = '_blank'
          anchor.rel = 'noopener'
          document.body.appendChild(anchor)
          anchor.click()
          document.body.removeChild(anchor)
          return order.downloadOrderArtifactSuccess({ orderId, artifact, link })
        }),
        // Inside the inner pipe: escaping into the outer mergeMap would tear the epic down for the
        // rest of the session, silently killing every later download.
        catchError((error) => {
          const status = statusOf(error)
          const failure = order.downloadOrderArtifactFailure({ orderId, artifact, error })
          if (status === 410) {
            reportHandledError('order-download', error, { orderId, artifact, status })
            return of(
              addMessage(
                errorMessage(`${label} is not available for this order. If you believe this is an error, please contact support.`)
              ),
              failure
            )
          }
          console.error(`Failed to download ${label}`, error)
          return of(addMessage(errorMessage(`${label} could not be downloaded. Please try again.`)), failure)
        })
      )
    })
  )
